import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { IEventDetails } from 'meta/backendControlled/structure'
import { ComponentsAdapter } from 'meta/backendControlled/structureBackend'
import { IPurchaseForm } from 'meta/pages/purchase'
import { ITicket, TicketAdapter } from 'meta/pages/tickets'
import { Routes } from 'meta/routes'
import { ApiEndpoint } from 'controller/endpoint'
import { useAppDispatch } from 'store'
import { LoginActions } from 'store/pages/login'
import { HttpRequest } from 'utils/http'

import { useIsAppInitialized } from 'hooks/useApp'

type Props = {
  authToken: string | undefined
  setPurchaseFormData: Dispatch<SetStateAction<IPurchaseForm | undefined>>
  setResponseReceived: Dispatch<SetStateAction<boolean>>
  setEvent: Dispatch<SetStateAction<IEventDetails | undefined>>
  setTickets: Dispatch<SetStateAction<ITicket[] | undefined>>
  transactionCode: string | null
  transactionToken: string | null
  responseReceived: boolean
}
export const useInitPurchaseForm = (props: Props) => {
  const {
    authToken,
    setPurchaseFormData,
    setResponseReceived,
    setEvent,
    setTickets,
    transactionCode,
    transactionToken,
    responseReceived,
  } = props

  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appInitialized = useIsAppInitialized()

  const [wasUserLoggedOut, setWasUserLoggedOut] = useState<boolean>(false)

  const callPurchaseFormEndpoint = async () => {
    try {
      const { data } = await HttpRequest.get<IPurchaseForm>(
        `${ApiEndpoint.purchase.form()}?transactionCode=${transactionCode}&transactionToken=${transactionToken}`
      )

      setResponseReceived(true)

      setPurchaseFormData(data)
      const tickets = TicketAdapter.convertResponse(data.items, data.groups)
      setTickets(tickets)
      const eventDetails = new ComponentsAdapter().convertComponent(data.details) as IEventDetails
      setEvent(eventDetails)

      //@ts-expect-error error type is unknown
    } catch (e: object) {
      const status = e.axiosError?.response?.status
      if (status === 401 && !authToken) {
        dispatch(LoginActions.show())
        setWasUserLoggedOut(true)
        return
      }

      let redirectPath
      if ([403, 412].includes(status)) {
        redirectPath = authToken ? Routes.ProfileTransactions.generatePath() : Routes.Success.generatePath()
      }
      if ([400, 401, 404, 500].includes(status)) {
        redirectPath = Routes.Error.generatePath(undefined, { error: String(e.message), errorCode: status })
      }
      if (redirectPath) {
        navigate(redirectPath, { replace: true })
      }
    }
  }

  useEffect(() => {
    if (!appInitialized || responseReceived) return
    ;(async () => {
      callPurchaseFormEndpoint()
    })()
  }, [appInitialized, responseReceived])

  useEffect(() => {
    if (wasUserLoggedOut && authToken) {
      setWasUserLoggedOut(false)
      callPurchaseFormEndpoint()
    }
  }, [wasUserLoggedOut, authToken])
}
